/**
 * Constants and utilities for Excel named range management
 * Used for bidirectional sync between Excel and Cosmic Frog database
 */

// Named range prefix for all Cosmic Frog cells
export const NAMED_RANGE_PREFIX = 'olf_';

// Minimum parts required in a cell name
export const CELL_NAME_MIN_PARTS = 5;

/**
 * Creates a named range identifier for a cell
 * Format: olf_{modelId}_{tableName}_{rowId}_{columnName}
 *
 * @param {string} modelId - The model GUID
 * @param {string} tableName - The table name
 * @param {string|number} rowId - The row ID
 * @param {string} columnName - The column name
 * @returns {string} The named range identifier
 */
export function createCellName(modelId, tableName, rowId, columnName) {
    const cleanModelId = modelId.replace(/-/g, '');
    return `${NAMED_RANGE_PREFIX}${cleanModelId}_${tableName}_${rowId}_${columnName}`;
}

/**
 * Handles the Excel API limitation where empty cells cannot have named ranges
 * This is a workaround: temporarily fill empty cells with "TEMP" before naming
 *
 * @param {Excel.Range} cell - The Excel cell to check
 * @returns {boolean} True if the cell was empty and needed temp value
 */
export function handleEmptyCellForNaming(cell) {
    const wasEmpty = !cell.values[0][0];
    if (wasEmpty) {
        cell.values = [["TEMP"]];
    }
    return wasEmpty;
}

/**
 * Restores an empty cell after named range creation
 * Call this after creating a named range if handleEmptyCellForNaming returned true
 *
 * @param {Excel.Range} cell - The Excel cell to restore
 * @param {boolean} wasEmpty - Whether the cell was originally empty
 */
export function restoreEmptyCell(cell, wasEmpty) {
    if (wasEmpty) {
        cell.values = [[""]];
    }
}

/**
 * Checks if a named range belongs to Cosmic Frog
 *
 * @param {string} rangeName - The named range identifier
 * @returns {boolean} True if this is a Cosmic Frog named range
 */
export function isCosmicFrogNamedRange(rangeName) {
    return rangeName.startsWith(NAMED_RANGE_PREFIX);
}
